<template>
  <section id="login">
    <div class="l-left-container">
      <img
        class="l-image"
        src="@shared/assets/img/logo/smac-sensors.png"
        alt="SmartAC.com Sensors"
      >
      <div class="l-slogan">
        <div>CHANGE IS</div>
        <div class="l-tm-container">
          IN THE AIR<span class="l-tm">&reg;</span>
        </div>
      </div>
    </div>
    <div class="l-right-container">
      <div class="l-header">
        Welcome back
      </div>
      <div class="l-sub-header">
        Enter the email address you used to set up your account.
      </div>
      <form class="l-form-container" @submit.prevent="login">
        <div class="l-input-container">
          <label for="email" class="l-label">
            Email
          </label>
          <input id="email" v-model="email" type="text" class="l-input">
          <div v-if="error" class="l-login-error">
            {{ error }}
          </div>
        </div>
        <button class="l-button" type="submit">
          <span v-if="!processing">SIGN IN WITH EMAIL</span>
          <img v-else src="@shared/assets/img/general/loading-white.svg" class="spinner" alt="Processing">
        </button>
      </form>
      <a href="https://smartac.com" class="l-footer-container">
        <img src="@shared/assets/img/logo/black-logo.png" class="logo" alt="SmartAC.com">
      </a>
    </div>
  </section>
</template>

<script>
import { postOAuthForm } from '@shared/lib/general/alphaAuth';

export default {
  name: 'LoginView',
  metaInfo: {
    title: 'SmartAC.com | Dashboard Login',
  },
  data() {
    return {
      email: localStorage.getItem('loginEmail'),
      error: '',
      processing: false,
    };
  },
  mounted() {
    const {
      query,
    } = this.$route;
    if (query?.error) {
      this.error = query.error;
    }
  },
  methods: {
    async login() {
      this.processing = true;
      try {
        localStorage.setItem('loginEmail', this.email);

        const [provider, pool] = await Promise.all([
          this.$store.dispatch('getUserProvider', this.email),
          this.$store.dispatch('getDomainPool', this.email),
        ]);
        if (pool) {
          localStorage.setItem('alphaDomainPool', JSON.stringify(pool));
        } else {
          localStorage.removeItem('alphaDomainPool');
        }

        const { hostname } = window.location;
        if (provider && !hostname.includes('vercel')) {
          postOAuthForm({
            provider,
            pool,
            redirectUri: `${window.location.origin}/login/verify`,
          });
          return;
        }
        this.$router.replace('/login-email');
      } finally {
        this.processing = false;
      }
    },
  },
};
</script>

<style lang="scss">
section#login {
  display: flex;
  width: 100%;
  height: 100%;
  font-family: 'Heebo', sans-serif;
  background-color: $WHITE;

  .l-left-container {
    background-color: $N110;
    flex: 1;
    min-height: 722px;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    justify-content: center;

    @media (max-width: 768px) {
      display: none;
    }

    .l-image {
      width: 50%;
      margin-bottom: 50px;

      @media (max-width: 1440px) {
        width: 70%;
      }
    }

    .l-slogan {
      width: 50%;
      font-size: 4.8125rem;
      color: $WHITE;
      font-weight: 800;
      line-height: 5rem;

      .l-tm-container {
        position: relative;

        .l-tm {
          font-size: 1rem;
          position: absolute;
          top: -24px;
        }
      }

      @media (max-width: 1550px) {
        font-size: 3.8rem;

        .l-tm-container {
          .l-tm {
            top: -15px;
          }
        }
      }

      @media (max-width: 1440px) {
        width: 70%;
      }
    }
  }

  .l-login-error {
    font-size: 0.75rem;
    color: $TOMATO100;
    text-align: center;
    margin-top: 15px;
    white-space: pre;
  }

  .l-right-container {
    max-width: 650px;
    min-height: 722px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 136px;

    @media (max-width: 768px) {
      padding: 0 100px;
      width: 100%;
      min-width: 0;
      min-height: 0;
    }

    @media (max-width: 420px) {
      padding: 0 40px;
    }

    @media (max-width: 320px) {
      padding: 0 25px;
    }

    .l-header {
      font-size: 3.375rem;
      color: $N100;
      margin-bottom: 4vh;
    }

    .l-sub-header {
      font-size: 1.5rem;
      color: $N100;
      margin-bottom: 6vh;
    }

    .l-external-container {
      .le-external-button {
        height: 57px;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 15px;
        border-radius: 10px;
        box-shadow: 0px 0px 3.189px 0px rgba(0, 0, 0, 0.08), 0px 2.126px 3.189px 0px rgba(0, 0, 0, 0.17);
        font-size: 1.3125rem;
        font-weight: 500;

        &.google {
          background-color: $WHITE;
          color: $N50;
        }
      }

      .external-logo {
        margin-right: 12px;
      }
    }

    .l-divider-container {
      display: flex;
      align-items: center;
      font-size: 1.125rem;

      .l-divider-line {
        flex: 1;
        border-bottom: 2px solid $N100;

        &:first-child {
          margin-right: 8px;
        }

        &:last-child {
          margin-left: 8px;
        }
      }
    }

    .l-form-container {
      .l-input-container {
        display: flex;
        flex-direction: column;
        width: 100%;
        margin-bottom: 5vh;

        .l-label {
          margin: 4px 0;
          font-size: 1rem;
          color: $N100;
          font-weight: 600;
          text-transform: uppercase;
        }

        .l-input {
          height: 56px;
          border: 2px solid $N10;
          border-radius: 4px;
          padding-left: 12px;
          font-size: 1.25rem;

          &:focus {
            border: 2px solid $NBLUE80;
          }

          @media (max-width: 320px) {
            height: 40px;
          }
        }
      }

      .l-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 56px;
        background-color: $NBLUE100;
        border-radius: 6px;
        color: $WHITE;
        font-size: 1rem;
        font-weight: 600;

        @media (max-width: 320px) {
          height: 40px;
        }
      }
    }

    .l-footer-container {
      display: flex;
      align-items: flex-end;
      justify-content: center;
      margin-top: 15vh;

      .logo {
        width: 184px;
        height: 21px;

        @media (max-width: 420px) {
          width: 120px;
          height: 15px;
        }

        @media (max-width: 320px) {
          width: 100px;
          height: 12px;
        }
      }
    }
  }
}
</style>
